import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { UserPlus, Check, Hourglass, UserCheck, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function FriendButton({ userId, status: initialStatus, requestId, compact = false, onChange }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [status, setStatus] = useState(initialStatus || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(initialStatus || null); }, [initialStatus]);

  if (!user || user.id === userId) return null;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/friends/request/${userId}`);
      setStatus(data.auto_accepted ? "friends" : "outgoing_pending");
      toast.success(data.auto_accepted ? t("friendBtn.autoAccepted") : t("friendBtn.sentToast"));
      onChange?.();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const accept = async () => {
    if (!requestId) return;
    setBusy(true);
    try {
      await api.post(`/friends/${requestId}/accept`);
      setStatus("friends");
      toast.success(t("friendBtn.added"));
      onChange?.();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!requestId) return;
    setBusy(true);
    try {
      await api.post(`/friends/${requestId}/reject`);
      setStatus("none");
      onChange?.();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unfriend = async () => {
    if (!confirm(t("friendBtn.removeConfirm"))) return;
    setBusy(true);
    try {
      await api.delete(`/friends/${userId}`);
      setStatus("none");
      onChange?.();
    } catch (e) { toast.error(fmtError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cls = compact ? "btn-nb !py-1.5 !px-3 !text-xs" : "btn-nb";
  const icon = (Icon) => <Icon className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} strokeWidth={2.5}/>;

  if (status === "friends") {
    return (
      <button onClick={unfriend} disabled={busy} className={cls} data-testid={`friend-btn-${userId}`}>
        {icon(UserCheck)} {t("friendBtn.friends")}
      </button>
    );
  }
  if (status === "outgoing_pending") {
    return (
      <button disabled className={cls} data-testid={`friend-btn-${userId}`}>
        {icon(Hourglass)} {t("friendBtn.sent")}
      </button>
    );
  }
  if (status === "incoming_pending") {
    return (
      <div className="flex gap-1">
        <button onClick={accept} disabled={busy} className={`${cls} btn-nb-primary`} data-testid={`friend-accept-${userId}`}>
          {icon(Check)} {t("friendBtn.accept")}
        </button>
        <button onClick={reject} disabled={busy} className={cls} data-testid={`friend-reject-${userId}`}>
          {icon(X)}
        </button>
      </div>
    );
  }
  return (
    <button onClick={send} disabled={busy} className={`${cls} btn-nb-info`} data-testid={`friend-btn-${userId}`}>
      {icon(UserPlus)} {t("friendBtn.add")}
    </button>
  );
}
